import React, { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import api, { fmtDateTime } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { PriorityBadge, StatusBadge, SlaChip, useSd, errText } from "@/components/servicedesk/SdBits";
import { ActionDialog, buildActions } from "@/components/servicedesk/TicketActions";

const Info = ({ t }) => (
  <dl className="grid grid-cols-2 gap-x-4 gap-y-2.5 text-sm" data-testid="sd-ticket-info">
    {[["Requester", t.requester_name], ["Position", t.requester_position], ["Company", t.company_name], ["Department", t.department], ["Location", t.location], ["Contact", t.contact],
      ["Category", `${t.category}${t.subcategory ? ` / ${t.subcategory}` : ""}`], ["Impact / Urgency", `${t.impact} / ${t.urgency}`], ["Affected asset", t.asset],
      ["Team", t.team_name], ["Support group", t.support_group], ["Technician", t.assignee_name], ["Preferred schedule", t.preferred_schedule], ["Escalation level", t.escalation_level || 0]].map(([k, v]) => (
      <div key={k}><dt className="text-[11px] uppercase tracking-wide text-slate-400">{k}</dt><dd className="text-slate-800 break-words">{v || "—"}</dd></div>
    ))}
  </dl>
);

const Sla = ({ s }) => (
  <div className="grid grid-cols-2 gap-3 text-xs" data-testid="sd-sla-panel">
    {[["Response", s.response_due, s.responded_at, s.response_breached, s.response_hours], ["Resolution", s.resolution_due, s.resolved_at, s.resolution_breached, s.resolution_hours]].map(([k, due, done, br, h]) => (
      <div key={k} className={`rounded-lg p-3 ${br ? "bg-red-50 text-red-800" : done ? "bg-emerald-50 text-emerald-800" : "bg-slate-50 text-slate-700"}`}>
        <div className="font-semibold">{k} · {h}h</div>
        <div>Due {fmtDateTime(due)}</div>
        <div>{done ? `Done ${fmtDateTime(done)}` : s.paused_at ? "Paused" : "Running"}{br ? " · BREACHED" : ""}</div>
      </div>
    ))}
    {s.paused_hours > 0 && <div className="col-span-2 text-slate-500">Paused for {s.paused_hours} business hour(s) in total</div>}
  </div>
);

const Approvals = ({ steps }) => !steps?.length ? null : (
  <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="sd-approvals">
    <h3 className="text-sm font-semibold mb-2">Approvals</h3>
    <ol className="space-y-2">{steps.map((s, i) => (
      <li key={`${s.label}-${i}`} className="flex items-start gap-2 text-xs">
        {s.status === "Approved" ? <Icons.CheckCircle2 className="h-4 w-4 text-emerald-500" /> : s.status === "Rejected" ? <Icons.XCircle className="h-4 w-4 text-red-500" /> : <Icons.Clock className="h-4 w-4 text-slate-400" />}
        <div><b>{s.label}</b> · {s.status}{s.by ? ` by ${s.by} · ${fmtDateTime(s.at)}` : ` · ${(s.approver_names || []).join(", ")}`}{s.reason ? <div className="text-slate-500">"{s.reason}"</div> : null}</div>
      </li>
    ))}</ol>
  </div>
);

const Thread = ({ d, staff, readOnly, reload }) => {
  const [body, setBody] = useState("");
  const [internal, setInternal] = useState(false);
  const [file, setFile] = useState(null);
  const send = async () => {
    try {
      if (body.trim()) await api.post(`/servicedesk/tickets/${d.ticket.id}/comments`, { body, internal });
      if (file) { const fd = new FormData(); fd.append("file", file); await api.post(`/servicedesk/tickets/${d.ticket.id}/attachments?internal=${internal}`, fd); }
      setBody(""); setFile(null); setInternal(false); reload();
    } catch (e) { toast.error(errText(e)); }
  };
  const closed = ["CLOSED", "CANCELLED"].includes(d.ticket.status) || readOnly;
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="sd-thread">
      <h3 className="text-sm font-semibold mb-3">Conversation</h3>
      <div className="space-y-3">
        {!d.comments.length && <p className="text-xs text-slate-400">No messages yet.</p>}
        {d.comments.map((c) => (
          <div key={c.id} className={`rounded-lg p-3 text-sm ${c.internal ? "bg-amber-50 border border-amber-200" : c.staff ? "bg-sky-50" : "bg-slate-50"}`} data-testid={`sd-comment-${c.id}`}>
            <div className="text-[11px] text-slate-500 mb-1 flex items-center gap-1.5">{c.internal && <span className="font-semibold text-amber-700 inline-flex items-center gap-1"><Icons.Lock className="h-3 w-3" />Internal note</span>}<b className="text-slate-700">{c.author}</b>{c.staff ? " · IT" : ""} · {fmtDateTime(c.created_at)}</div>
            <div className="whitespace-pre-wrap break-words">{c.body}</div>
          </div>
        ))}
      </div>
      {!!d.attachments.length && (
        <div className="mt-4 flex flex-wrap gap-2" data-testid="sd-attachments">
          {d.attachments.map((a) => (
            <button key={a.id} onClick={() => api.get(`/servicedesk/attachments/${a.id}`, { responseType: "blob" }).then((r) => {
              const u = URL.createObjectURL(r.data);
              if (/^(image\/(png|jpeg|gif|webp)|application\/pdf)$/.test(r.data.type)) { window.open(u, "_blank"); return; }
              const l = document.createElement("a"); l.href = u; l.download = a.name; l.click(); setTimeout(() => URL.revokeObjectURL(u), 5000);
            })}
              className={`text-xs inline-flex items-center gap-1 rounded-full border px-2.5 py-1 hover:bg-slate-50 ${a.internal ? "border-amber-300" : "border-slate-200"}`} data-testid={`sd-attachment-${a.id}`}>
              <Icons.Paperclip className="h-3 w-3" />{a.name} · {Math.ceil(a.size / 1024)} KB
            </button>
          ))}
        </div>
      )}
      {!closed && (
        <div className="mt-4 space-y-2">
          <Textarea rows={3} value={body} onChange={(e) => setBody(e.target.value)} placeholder={internal ? "Internal note — only IT staff can see this" : "Write a reply"} data-testid="sd-reply" />
          <div className="flex flex-wrap items-center gap-3">
            {internal && <span className="text-[11px] font-semibold text-amber-700" data-testid="sd-internal-warning">Only IT staff will see this</span>}
            {staff && <label className="flex items-center gap-2 text-xs text-slate-600"><Switch checked={internal} onCheckedChange={setInternal} data-testid="sd-internal-toggle" />Internal note</label>}
            <input type="file" onChange={(e) => setFile(e.target.files[0] || null)} className="text-xs" data-testid="sd-reply-file" />
            <Button size="sm" className="ml-auto" onClick={send} disabled={!body.trim() && !file} data-testid="sd-send-btn"><Icons.Send className="h-3.5 w-3.5 mr-1" />Send</Button>
          </div>
        </div>
      )}
    </div>
  );
};

const History = ({ items }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="sd-history">
    <h3 className="text-sm font-semibold mb-2">History</h3>
    <ol className="relative border-l border-slate-200 ml-1.5 space-y-2.5">
      {items.map((h, i) => (
        <li key={`${h.at}-${i}`} className="pl-3 text-xs"><span className="absolute -left-[5px] mt-1 h-2.5 w-2.5 rounded-full bg-slate-300" />
          <b className="text-slate-700">{h.action}</b>{h.to_status ? ` → ${h.to_status}` : ""}{h.to_priority ? ` ${h.from_priority} → ${h.to_priority}` : ""}
          <div className="text-slate-500">{h.by} · {fmtDateTime(h.at)}</div>{h.note && <div className="text-slate-600">{h.note}</div>}
        </li>
      ))}
    </ol>
  </div>
);

export default function SdTicketDetail() {
  const { id } = useParams();
  const { me, cat } = useSd();
  const [d, setD] = useState(null);
  const [staff, setStaff] = useState([]);
  const [teams, setTeams] = useState([]);
  const [dlg, setDlg] = useState(null);
  const load = useCallback(() => api.get(`/servicedesk/tickets/${id}`).then(({ data }) => setD(data)).catch((e) => setD({ error: errText(e, "Ticket not found") })), [id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!me?.is_staff) return;
    api.get("/servicedesk/staff").then(({ data }) => setStaff(data)).catch(() => {});
    api.get("/servicedesk/teams").then(({ data }) => setTeams(data)).catch(() => {});
  }, [me]);
  if (!d || !me || !cat) return <div className="p-6 text-sm text-slate-500">Loading…</div>;
  if (d.error) return <div className="p-6 text-sm text-red-600" data-testid="sd-ticket-error">{d.error}</div>;
  const t = d.ticket;
  const actions = buildActions({ t, can: d.can, me, cat, staff, teams });
  return (
    <div data-testid="sd-ticket-page">
      <Link to={me.is_staff ? "/servicedesk/queue" : "/servicedesk/my"} className="text-xs text-slate-500 hover:text-slate-800 inline-flex items-center gap-1 mb-3"><Icons.ArrowLeft className="h-3.5 w-3.5" />Back to tickets</Link>
      <div className="bg-white rounded-xl border border-slate-200 p-5 mb-4">
        <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500"><span className="font-mono" data-testid="sd-ticket-number">{t.number}</span>· {t.type}<PriorityBadge p={t.priority} /><StatusBadge s={t.status} /><SlaChip t={t} />
          {t.is_major && <span className="rounded bg-red-600 text-white px-1.5 py-0.5 text-[10px] font-bold" data-testid="sd-major-badge">MAJOR INCIDENT</span>}</div>
        <h1 className="text-xl sm:text-2xl font-semibold text-slate-900 mt-2" data-testid="sd-ticket-subject">{t.subject}</h1>
        {t.requester_left && <div className="mt-2 rounded-lg bg-rose-50 border border-rose-200 px-3 py-2 text-xs text-rose-800" data-testid="sd-requester-left">The requester has left the company (HRIS). Close this ticket or hand it to someone else.</div>}
        <p className="text-sm text-slate-600 mt-2 whitespace-pre-wrap">{t.description}</p>
        {t.justification && <p className="text-sm text-slate-600 mt-2"><b>Justification:</b> {t.justification}</p>}
        {d.parent && <p className="text-xs mt-2">Linked to <Link className="text-sky-700 hover:underline" to={`/servicedesk/tickets/${d.parent.id}`}>{d.parent.number} · {d.parent.subject}</Link></p>}
        {!!actions.length && (
          <div className="flex flex-wrap gap-2 mt-4" data-testid="sd-actions">
            {actions.map((a) => { const I = Icons[a.icon] || Icons.Circle; return <Button key={a.key} size="sm" variant={a.tone} onClick={() => setDlg(a.cfg)} data-testid={`sd-action-${a.key}`}><I className="h-3.5 w-3.5 mr-1" />{a.label}</Button>; })}
          </div>
        )}
      </div>
      <div className="grid lg:grid-cols-[1fr_360px] gap-4">
        <div className="space-y-4 min-w-0">
          {t.resolution && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-sm" data-testid="sd-resolution">
              <h3 className="font-semibold text-emerald-900 mb-1">Resolution · {t.resolution.resolved_by} · {fmtDateTime(t.resolution.resolved_at)}</h3>
              {t.resolution.root_cause && <p><b>Root cause:</b> {t.resolution.root_cause}</p>}<p>{t.resolution.resolution}</p>
              {t.resolution.actions && <p><b>Actions:</b> {t.resolution.actions}</p>}{t.resolution.parts && <p><b>Parts/changes:</b> {t.resolution.parts}</p>}
              {t.resolution.post_incident_report && <p><b>Post-incident report:</b> {t.resolution.post_incident_report}</p>}
              {t.feedback && <p className="mt-1 text-emerald-800">Rated {t.feedback.overall}/5 by requester{t.feedback.comment ? ` — "${t.feedback.comment}"` : ""}</p>}
            </div>
          )}
          <Thread d={d} staff={me.is_staff} readOnly={me.role === "Auditor"} reload={load} />
          {!!d.children.length && (
            <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="sd-children"><h3 className="text-sm font-semibold mb-2">Linked tickets ({d.children.length})</h3>
              {d.children.map((k) => <Link key={k.id} to={`/servicedesk/tickets/${k.id}`} className="block text-xs text-sky-700 hover:underline py-0.5">{k.number} · {k.subject} · {k.requester_name} · {k.status}</Link>)}</div>
          )}
        </div>
        <div className="space-y-4">
          <div className="bg-white rounded-xl border border-slate-200 p-4"><h3 className="text-sm font-semibold mb-3">SLA</h3><Sla s={t.sla} /></div>
          <Approvals steps={t.approvals} />
          <div className="bg-white rounded-xl border border-slate-200 p-4"><h3 className="text-sm font-semibold mb-3">Details</h3><Info t={t} /></div>
          <History items={d.history} />
        </div>
      </div>
      {dlg && <ActionDialog cfg={dlg} onClose={() => setDlg(null)} onDone={load} />}
    </div>
  );
}
